import type { ComponentProps } from 'react';

import {
  Button as ButtonPrimitive,
  type ButtonProps as ButtonPrimitiveProps,
} from '@/components/buttons';

import { useFormContext } from './form-context';

type AutoDisableProps = {
  autoDisable?: boolean;
};

export type ButtonProps = ButtonPrimitiveProps & AutoDisableProps;

export const Button = ({ disabled, autoDisable = true, ...props }: ButtonProps) => {
  const form = useFormContext();

  return (
    <form.Subscribe selector={(state) => state.isSubmitting}>
      {(isSubmitting) => (
        <ButtonPrimitive {...props} disabled={disabled || (autoDisable && isSubmitting)} />
      )}
    </form.Subscribe>
  );
};

export type SubmitButtonProps = Omit<ButtonPrimitiveProps, 'isLoading' | 'type'>;
export const SubmitButton = ({ children, onClick, disabled, ...props }: SubmitButtonProps) => {
  const form = useFormContext();

  const handleClick: ButtonPrimitiveProps['onClick'] = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (onClick) {
      onClick(event);
    }
    form.handleSubmit();
  };

  return (
    <form.Subscribe selector={(state) => state.isSubmitting}>
      {(isSubmitting) => (
        <ButtonPrimitive
          {...props}
          type="submit"
          disabled={disabled}
          isLoading={isSubmitting}
          onClick={handleClick}
        >
          {children}
        </ButtonPrimitive>
      )}
    </form.Subscribe>
  );
};

export type ResetButtonProps = Omit<
  ComponentProps<typeof ButtonPrimitive>,
  'disabled' | 'isLoading' | 'type'
>;

export const ResetButton = ({ children, ...props }: ResetButtonProps) => {
  const form = useFormContext();
  const { onClick, ...buttonProps } = props;

  return (
    <form.Subscribe selector={(state) => state.isSubmitting || !state.isDirty}>
      {(disabled) => (
        <ButtonPrimitive
          type="button"
          disabled={disabled}
          onClick={(event) => {
            form.reset();
            onClick?.(event);
          }}
          {...buttonProps}
        >
          {children}
        </ButtonPrimitive>
      )}
    </form.Subscribe>
  );
};
